import {
  collection,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  increment,
} from "firebase/firestore";

import imageCompression from "browser-image-compression";

import { db, auth } from "../firebase";

const CITIES_WITH_DISTRICTS = ["tbilisi"];
const CITIES_WITH_LOCALAREAS = ["batumi", "kutaisi"];

const getUserUsername = async (uid) => {
  const userSnap = await getDoc(
    doc(db, "publicUsers", uid)
  );

  if (userSnap.exists()) {
    return userSnap.data().username || "User";
  }

  return "User";
};

export async function publishListing({
  listing,
  user,
  isEditMode,
  editingListing,
  submitting,
  setSubmitting,
  navigate,
  t,
  upgradeType = null,
  days = 1,
}) {

  console.log("=================================");
console.log("publishListing() CALLED");
console.log("listing:", listing);
console.log("upgradeType:", upgradeType);
console.log("=================================");

  if (submitting) return;

  if (!user) {
    alert("You must be logged in to post a listing");
    return;
  }

  // ===============================
  // VALIDATION
  // ===============================

  if (!listing.type) {
    alert("Property type is required");
    return;
  }

  if (!listing.purpose) {
    alert("Purpose is required");
    return;
  }

  if (!listing.lat || !listing.lng) {
    alert("Please set location on map");
    return;
  }

  if (!listing.city) {
    alert("City / municipality is required");
    return;
  }

  if (!listing.manualAddress && !listing.lat) {
    alert("Please select location on map");
    return;
  }

  if (
    CITIES_WITH_DISTRICTS.includes(listing.city) &&
    !listing.district
  ) {
    alert("District is required");
    return;
  }

  if (
    CITIES_WITH_LOCALAREAS.includes(listing.city) &&
    !listing.localarea
  ) {
    alert("Area is required");
    return;
  }

  if (listing.manualStreet) {

    if (!listing.street?.en && !listing.street?.ka) {
      alert("Street name is required");
      return;
    }

    if (!listing.streetNumber) {
      alert("Street number is required");
      return;
    }
  }

  if (
    !Number.isFinite(listing.price_gel) ||
    listing.price_gel <= 0
  ) {
    alert("Valid price is required");
    return;
  }

  if (!listing.area || Number(listing.area) <= 0) {
    alert("Valid area is required");
    return;
  }

  // ===============================
  // BUILDING FIELDS
  // ===============================

  if (listing.type !== "land") {

    if (
      listing.type !== "commercial_space" &&
      (listing.bedrooms == null ||
        listing.bedrooms === "")
    ) {
      alert("Bedrooms are required");
      return;
    }

    if (
      listing.condition === "green_frame" &&
      (listing.renovation == null ||
        listing.renovation === "")
    ) {
      alert(
        "Renovation is required for green frame"
      );
      return;
    }

    if (!listing.building) {
      alert("Building type is required");
      return;
    }

    if (
      listing.rooms == null ||
      listing.rooms === ""
    ) {
      alert("Number of rooms is required");
      return;
    }

    if (
      listing.type !== "commercial_space" &&
      (listing.bathrooms == null ||
        listing.bathrooms === "")
    ) {
      alert("Bathrooms are required");
      return;
    }
  }

  // ===============================
  // DESCRIPTION
  // ===============================

  if (
    !listing.description ||
    listing.description.trim().length < 10
  ) {
    alert(t("listing.descriptionRequired"));
    return;
  }

  // ===============================
  // PHOTOS
  // ===============================

  if (
    !listing.photos ||
    listing.photos.length === 0
  ) {
    alert(t("listing.photoRequired"));
    return;
  }

  // ===============================
  // PHONE
  // ===============================

  if (
    !listing.phone ||
    listing.phone.trim().length < 9
  ) {
    alert(t("listing.phoneRequired"));
    return;
  }

  try {

    // ===============================
    // USER PLAN LIMITS
    // ===============================

    const now = Date.now();

    const userRef = doc(
      db,
      "users",
      auth.currentUser.uid
    );

    const snap = await getDoc(userRef);

    const data = snap.data() || {};

    const plan = data.plan || "free";

    const trialEndAt = Number(
      data.trialEndAt || 0
    );

    const isTrialActive =
      plan === "gift_trial" &&
      now < trialEndAt;

    let limit = 3;

    if (isTrialActive) {
      limit = 9999;
    }

    const limitlessUntil = Number(data.limitlessUntil || 0);

if (
    plan === "limitless" &&
    now < limitlessUntil
) {
    limit = 999999;
}

    const MONTH_MS =
      30 * 24 * 60 * 60 * 1000;

    let count = data.monthlyPostCount ?? 0;

    let resetAt = data.monthlyPostResetAt;

    if (!resetAt || now >= resetAt) {

      count = 0;

      resetAt = now + MONTH_MS;

      await updateDoc(userRef, {
        monthlyPostCount: 0,
        monthlyPostResetAt: resetAt,
      });
    }


    setSubmitting(true);

    // ===============================
    // UPLOAD IMAGES
    // ===============================

    let uploadedPhotoUrls = listing.photos
      .filter(
        (p) =>
          typeof p === "string" || p?.url
      )
      .map((p) =>
        typeof p === "string" ? p : p.url
      );

    const hasNewFiles = listing.photos?.some(
      (p) => p.file
    );

    if (hasNewFiles) {

      const formData = new FormData();

      const compressedFiles =
        await Promise.all(
          listing.photos.map(async (photoObj) => {

            if (!photoObj.file) return null;

            try {

              return await imageCompression(
                photoObj.file,
                {
                  maxSizeMB: 1,
                  maxWidthOrHeight: 1200,
                  useWebWorker: true,
                }
              );

            } catch {

              return photoObj.file;
            }
          })
        );

      compressedFiles
        .filter(Boolean)
        .forEach((file) => {
          formData.append("files", file);
        });

      const res = await fetch(
        "https://vipart-backend.onrender.com/upload",
        {
          method: "POST",
          body: formData,
        }
      );

      const uploadData = await res.json();

      console.log(
        "UPLOAD RESPONSE:",
        uploadData
      );

      if (!res.ok) {
        alert("Upload failed");
        return;
      }

      uploadedPhotoUrls = [
        ...uploadedPhotoUrls,
        ...(uploadData.urls || []),
      ];
    }

    const safePhotos =
      uploadedPhotoUrls.filter(Boolean);

    const { photos, ...rest } = listing;

    const firebaseUser = auth.currentUser;

    if (!firebaseUser?.uid) {
      alert(
        "User not authenticated properly"
      );
      return;
    }

    const usernameFromDb =
      await getUserUsername(firebaseUser.uid);

    


const LISTING_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

const expiresAt = isEditMode
  ? editingListing.expiresAt ?? (
      new Date(editingListing.createdAt).getTime() +
      LISTING_DURATION_MS
    )
  : Date.now() + LISTING_DURATION_MS;




// ===============================
    // CREATE LISTING OBJECT
    // ===============================

    const newListing = {
      ...rest,

      userId: firebaseUser.uid,

      username: usernameFromDb,

      accountType: user?.accountType || "regular",

      createdAtUser:
        user?.createdAt || "",

      city: listing.city,

      district:
        listing.district || "",

      neighborhood:
        listing.neighborhood || "",

      localarea:
        listing.localarea || "",

      price_gel: Number(
        listing.price_gel
      ),

      area: Number(listing.area),

      rooms:
        listing.rooms === "" ||
        listing.rooms == null
          ? null
          : listing.rooms,

      bedrooms:
        listing.bedrooms === "" ||
        listing.bedrooms == null
          ? null
          : listing.bedrooms,

      yardArea:
        listing.yardArea === "" ||
        listing.yardArea == null
          ? null
          : Number(listing.yardArea),

      bathrooms:
        listing.bathrooms === "" ||
        listing.bathrooms == null
          ? null
          : String(listing.bathrooms),

      photos: safePhotos,

      description:
        listing.description,

      status: "draft",

      upgradeType,

      createdAt: isEditMode
        ? editingListing.createdAt
        : new Date().toISOString(),
    

expiresAt,


};



        // ===============================
    // UPDATE EXISTING
    // ===============================

    if (isEditMode) {
      const docRef = doc(
        db,
        "listings",
        editingListing.id
      );

      await updateDoc(docRef, {
        ...newListing,

        // preserve existing publication / promotion state
        status: editingListing.status ?? "active",
        upgradeType: editingListing.upgradeType ?? null,

        // preserve promotion timers if they exist
        vipUntil: editingListing.vipUntil ?? null,
        goldUntil: editingListing.goldUntil ?? null,
        extraGoldUntil: editingListing.extraGoldUntil ?? null,

        updatedAt: new Date().toISOString(),
      });

      alert("Listing updated");

    navigate(`/listing/${editingListing.id}`, {
    replace: true,
    state: { from: "/my-listings" },
    });

      return;
    } else {

      // ===============================
      // CREATE NEW
      // ===============================

      let docRef;

      if (!upgradeType) {

        if (count >= limit) {

          // create draft listing first
          docRef = await addDoc(
            collection(db, "listings"),
            {
              ...newListing,
              status: "draft",
            }
          );

          // send user to payment page with listing id
          navigate("/trial-ended", {
            state: {
              listingId: docRef.id,
            },
          });

          return;
        }

        docRef = await addDoc(
          collection(db, "listings"),
          {
            ...newListing,
            status: "draft",
          }
        );

        await updateDoc(userRef, {
          monthlyPostCount: increment(1),
          monthlyPostResetAt: resetAt,
        });

        navigate("/listing-upgrade", {
  replace: true,
  state: { listingId: docRef.id },
});

        return;
      }
    }


} catch (error) {
  console.error("publishListing error:", error);
  alert("Something went wrong");
} finally {
  setSubmitting(false);
}

} // closes publishListing()
